import React, { useReducer, useEffect, useRef } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import Select from "react-select";
import { getAuthToken } from "../api/api";
import Tooltip from "../components/common/ToolTip";
import {DraggableModalDialog} from "../components/common/DraggableModal"
import {
  getAPIUrl,
  ValidateInputField,
  notify,
} from "../components/common/helpers";
import { getUserDetails } from "../components/sidebar/Sidenav";
import { ReactComponent as Close } from "../../src/icons-svg/icons/close.svg";
import "../styles/Modal.css";

const User = (props) => {
  const { disabledFeatures, getUsers, isEdit, setEdit, rowEntries } = props;
  const inputRef = useRef(null);
  const controller = new AbortController();
  const formReducer = (state, action) => {
    switch (action.type) {
      case "SET_FIELD_VALUE":
        return {
          ...state,
          formData: {
            ...state.formData,
            [action.field]: action.value,
          },
        };

      case "SET_FORM_ERRORS":
        return {
          ...state,
          formErrors: {
            ...state.formErrors,
            [action.field]: action.error,
          },
        };
      case "SET_VALIDATED":
        return {
          ...state,
          validated: action.validated,
        };
      case "SET_SELECTED_VALUE":
        return {
          ...state,
          selectedValue: action.selectedValue,
        };
      case "SET_SHOW":
        return {
          ...state,
          show: action.show,
        };
      case "SET_OPTION":
        return {
          ...state,
          option: action.option,
        };
      case "RESET_FORM":
        return initialState;

      default:
        return state;
    }
  };

  const initialState = {
    formData: {
      userid: isEdit ? rowEntries.user_id : "",
      username: isEdit ? rowEntries.user_name : "",
    },
    formErrors: {
      userid: "",
      username: "",
    },
    validated: false,
    selectedValue: isEdit ? rowEntries.role_name : "",
    option: [],
    show: false,
  };
  
  const [state, dispatch] = useReducer(formReducer, initialState);
  const {
    formData,
    formErrors,
    validated,
    selectedValue,
    option,
    show,
  } = state;
  
  const handleClose = () => {
    if(controller) {
      controller.abort();
    }
    dispatch({ type: "SET_SHOW", show: false });
    dispatch({ type: "RESET_FORM" });
    isEdit && setEdit(false);
  };

  const handleShow = () => dispatch({ type: "SET_SHOW", show: true });

  const defaultValidation = () => {
   return Object.values(formErrors).every((error) => error === "valid" || error === "") &&
   formData.userid &&
   formData.username
  }

  const isFormValid = () => {
    if (isEdit) {
      return defaultValidation() &&
      selectedValue !== initialState["selectedValue"]
    } else {
      return defaultValidation()
    }
  };

  //Add a function to handle changes to the email input
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const error = ValidateInputField(name, value);
    dispatch({ type: "SET_FIELD_VALUE", field: name, value });
    dispatch({ type: "SET_FORM_ERRORS", field: name, error });
  };

  const formatFormData = (data) => {
    return data.charAt(0).toUpperCase() + data.slice(1);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const formData = new FormData(event.target);
    const userid = (isEdit !== null && isEdit) ? "updated_by" : "created_by";
    formData.append(userid, getUserDetails().username);
    formData.append("language", "English");
    const formDataObj = Object.fromEntries(formData.entries());
    formDataObj.username = formatFormData(formDataObj.username);
    formDataObj.userid = formatFormData(formDataObj.userid);

    if (isEdit !== null && isEdit) {
      try {
        axios({
          method: "POST",
          url: getAPIUrl("updateUserDetails"),
          signal: controller.signal,
          headers: {
            Authorization: getAuthToken(),
          },
          data: formDataObj,
        })
          .then((res) => {
            if (res) {
              handleClose();
              alert("User Details Updated Successfully");
              setTimeout(() => {
                getUsers();
              }, 3000);
            }
          })
          .catch((error) => {
            error.message !== "canceled" && notify("updating");
          });
      } catch (error) {
        console.log("Error fetching data:" + error);
      }
    } else {
      axios({
        method: "POST",
        signal: controller.signal,
        url: getAPIUrl("addUserDetailsApi"),
        headers: { Authorization: getAuthToken()},
        data: formDataObj,
      })
        .then((res) => {
          if (res && !res.data.code) {
            handleClose();
            alert("User Added Successfully");
            getUsers();
          } else {
            alert("User Already Exists");
          }
        })
        .catch((error) => {
          error.message !== "canceled" && notify("submission");
        });
    }
    dispatch({ type: "SET_VALIDATED", validated: false });
  };

  useEffect(() => {
    if (show || isEdit) {
      inputRef.current.focus();
      axios({
        method: "GET",
        url: getAPIUrl("getUserRolesApi"),
        headers: { Authorization: getAuthToken() },
        signal: controller.signal,
      })
        .then((res) => {
          let formatOption;
          if (res && res.data.length > 0) {
            formatOption = res.data.map((item) => ({
              value: item.role_id,
              label: item.role_name,
            }));
          } else {
            formatOption = [];
          }
          dispatch({ type: "SET_OPTION", option: formatOption });
        })
        .catch((error) => {
          dispatch({ type: "SET_OPTION", option: [] });
          console.log(error);
        });
    }
      return () => {
        if (!show) {
          controller.abort();
        }
      };
  }, [show]);

  const handleChange = (e) => {
    dispatch({ type: "SET_SELECTED_VALUE", selectedValue: e.label });
  };

  const setRoleDefaultValue = () => {
    if (isEdit) {
      return option.filter((obj) => selectedValue === obj.label);
    } else if (selectedValue !== "") {
        return option.filter((obj) => selectedValue === obj.label);
      } else {
        return option[0];
      }
  };

  return (
    <>
      {" "}
      {!disabledFeatures.includes("AddUser") && !isEdit && (
        <Button
          size="sm"
          variant="secondary"
          onClick={handleShow}
          style={{ background: "#ffffff", color: "#000000", width: "120px" }}
          disabled={disabledFeatures.includes("AddUser")}
        >
          +Add a User
        </Button>
      )}
      <Modal
        show={show || isEdit}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard={false}
        dialogAs={DraggableModalDialog} 
      >
        <Modal.Header className="cancelButton">
          <Modal.Title style={{ color: "#ffffff" }}>
            <Row>
            <Col sm={{ span: 7 }}>
            <h3>{!isEdit ? "Add User" : "Edit User Details"}</h3>
            </Col>
            <Col sm={{ offset: 3 }}>
                <Button
                    className="col-md-8 hoverEffect"
                    size="sm" 
                    variant="Dark"
                    onClick={handleClose}
                    aria-label="Close Button"
                  >
                    <Close/>
                  </Button>
                  </Col>
            </Row>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#212529" }}>
          <Form validated={validated} onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Form.Group
                as={Row}
                md="12"
                className="formField"
              >
                <Form.Label
                  className="required"
                  style={{ color: "white" }}
                  size="sm"
                  column
                  sm={4}
                  htmlFor="user name"
                >
                  User Name
                </Form.Label>
                <Col sm={8}>
                  <Form.Control
                    ref = {inputRef}
                    size="sm"
                    aria-required
                    type="text"
                    placeholder="User name"
                    id="validationCustom01"
                    name="username"
                    readOnly={isEdit}
                    value={formData.username}
                    onChange={handleInputChange}
                    aria-invalid={formErrors.username === "error"}
                    isInvalid={formErrors.username === "error"}
                    isValid={formErrors.username === "valid"}
                  />
                  {formErrors.username === "error" && (
                    <Tooltip type="username" charLength={2} required={true}/>
                  )}
                </Col>
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group
                as={Row}
                md="12"
                className="formField"
              >
                <Form.Label
                  className="required"
                  style={{ color: "white" }}
                  size="sm"
                  column
                  sm={4}
                  htmlFor="user email"
                >
                  User Email
                </Form.Label>
                <Col sm={8}>
                  <Form.Control
                    size="sm"
                    aria-required
                    type="email"
                    placeholder="John.doe@otis.com"
                    id="validationCustom02"
                    name="userid"
                    readOnly={isEdit}
                    value={formData.userid}
                    onChange={handleInputChange}
                    aria-invalid={formErrors.userid === "error"}
                    isInvalid={formErrors.userid === "error"}
                    isValid={formErrors.userid === "valid"}
                  />
                  {formErrors.userid === "error" && (
                    <Tooltip type="otis_id" required={true}/>
                  )}
                </Col>
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Row} md="12" className="formField">
                <Form.Label
                  className="required"
                  style={{ color: "white" }}
                  size="sm"
                  column
                  sm={4}
                  htmlFor="user role"
                >
                  User Role
                </Form.Label>
                <Col sm={8}>
                  <Select
                    size="sm"
                    aria-invalid={formErrors.site}
                    isInvalid={formErrors.site}
                    value={setRoleDefaultValue()} // set selected values
                    options={option} // set list of the data
                    onChange={handleChange} // assign onChange function
                    aria-required
                    name="roleid"
                  />
                </Col>
              </Form.Group>
            </Row>

            <Row>
              <Col sm={{ span: 10, offset: 2 }}>
                <Button
                  className="col-md-5"
                  size="sm"
                  variant="secondary"
                  type="submit"
                  disabled={!isFormValid()}
                >
                  {!isEdit ? "Submit" : "Update"}
                </Button>{" "}
                <Button
                  className="col-md-5"
                  size="sm"
                  variant="secondary"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default User;
